/**
 * Afriqahome Saved Properties API
 * P1.1: Database-backed saved properties (replaces localStorage)
 *
 * SYND-15 — POLYMORPHIC. Every function here takes a `SavedTarget`
 * (`{ kind: 'listing' | 'syndicated', id }`) rather than a bare uuid, because
 * `public.saved_properties` now carries two target columns and exactly one of
 * them is set per row:
 *
 *   listing_id    uuid NULL REFERENCES listings(id)            ON DELETE CASCADE
 *   syndicated_id uuid NULL REFERENCES syndicated_listings(id) ON DELETE CASCADE
 *   CHECK (num_nonnulls(listing_id, syndicated_id) = 1)
 *
 * The bare-uuid signature this replaced could not express which column a caller
 * meant, and the two id spaces are both uuids from different tables — so it
 * could not be made polymorphic by inspection either. A `SavedTarget` makes the
 * kind a compile-time requirement at every call site.
 *
 * TOKENS COME FROM getValidAccessToken(), NEVER FROM localStorage DIRECTLY.
 * That is the canon (see the note in lib/api/welcomeTierApi.ts and BUG-075 in
 * components/AgentProfileForm.tsx) and it is not cosmetic here: the previous
 * spelling read `afriqahome_access_token` raw, so a save attempted with an
 * expired-but-present token 401'd instead of refreshing — which the caller
 * reports as "save failed" and the user sees as a heart that fills and empties
 * again. `getValidAccessToken()` refreshes inside the five-minute window.
 */

import { apiGet, apiPost, apiDelete } from './client';
import { getValidAccessToken } from './authApi';
import {
  savedTargetColumn,
  savedTargetFromRow,
  type SavedTarget,
} from './savedTargets';

export type { SavedTarget, SavedTargetKind } from './savedTargets';
export { savedListing, savedSyndicated, savedTargetKey } from './savedTargets';

interface DbSavedProperty {
  id: string;
  user_id: string;
  listing_id: string | null;
  /**
   * SYND-15. Null on every marketplace row — and ABSENT, not null, on every row
   * read before the hand-run migration lands. Optional for that window; see the
   * `select=*` note on getSavedTargets.
   */
  syndicated_id?: string | null;
  created_at: string;
}

/**
 * PostgREST-encode a uuid used as a filter value.
 *
 * These are uuids from our own database, so in practice there is nothing to
 * escape — but they arrive here through props and route params, and a filter
 * built by string concatenation is the one place where "in practice" is not a
 * good enough reason to skip it.
 */
const eq = (column: string, value: string) => `${column}=eq.${encodeURIComponent(value)}`;

/**
 * Every target this user has saved, OLDEST FIRST.
 *
 * The order is explicit and it matters: with no ORDER BY, PostgREST returns
 * rows in whatever order the planner produced them, and pages/SavedProperties
 * derives "Recently Saved" from each item's position in this array. `asc` is
 * what makes later-in-the-array mean more-recently-saved, which is the reading
 * that sort has always assumed and never actually had.
 *
 * `select=*` RATHER THAN `select=listing_id,syndicated_id`, AND THAT IS A
 * DEPLOY-ORDER DECISION, NOT A STYLE ONE.
 *   The migration that adds `syndicated_id` is run BY HAND in the Supabase
 *   Dashboard (supabase/migrations/20260820100000_synd15_polymorphic_saves.sql),
 *   so there is a window in which this code is live and the column is not.
 *   Naming the column in the select makes that window a 42703 for the whole
 *   query — which this function reports by returning `[]`, indistinguishable
 *   from "you have saved nothing". Every heart in the product would read
 *   unsaved and /saved would show its empty state, with nothing in the UI
 *   saying why.
 *
 *   `*` degrades instead: pre-migration the rows come back without
 *   `syndicated_id`, `savedTargetFromRow` reads it as undefined, and the
 *   marketplace half behaves exactly as it did before this ticket. The table is
 *   five narrow columns, so there is no width being paid for.
 *
 *   This does NOT make the deploy order optional — a save of a syndicated entry
 *   still 42703s on the INSERT until the migration runs, and correctly shows
 *   the heart rolling back. It makes the failure local to the new feature
 *   instead of taking the old one down with it.
 *
 * A row whose target cannot be read is dropped rather than guessed at — see
 * savedTargetFromRow.
 *
 * `null` MEANS THE REQUEST FAILED. `[]` means the buyer has saved nothing.
 *   These were the same value before SYND-15 and the collapse was invisible,
 *   because nothing downstream did anything with an empty list. It is visible
 *   now: AuthContext mirrors the settled list to localStorage, so "failed"
 *   returning `[]` would persist an empty list over the offline backup — the
 *   one thing that backup exists to survive. It would also make the catch that
 *   reads it unreachable, since apiGet catches its own network exceptions and
 *   this function therefore never throws.
 *
 *   Both failure modes return null: no usable token (the refresh failed) and a
 *   PostgREST error. Neither is evidence about what the buyer has saved.
 */
export async function getSavedTargets(userId: string): Promise<SavedTarget[] | null> {
  const accessToken = await getValidAccessToken();

  if (!accessToken) {
    console.warn('getSavedTargets: No access token');
    return null;
  }

  const { data, error } = await apiGet<DbSavedProperty[]>(
    'saved_properties',
    `${eq('user_id', userId)}&select=*&order=created_at.asc`,
    accessToken
  );

  if (error || !data) {
    console.error('Failed to fetch saved properties:', error);
    return null;
  }

  return data
    .map(savedTargetFromRow)
    .filter((target): target is SavedTarget => target !== null);
}

/**
 * Save one target.
 *
 * A duplicate is success, not failure. The two partial unique indexes added by
 * SYND-15 (`saved_properties_user_listing_uq` / `…_user_syndicated_uq`) turn a
 * double-tap into 23505, and "you already saved this" is exactly the state the
 * caller was trying to reach — so it must not roll an optimistic heart back.
 */
export async function saveProperty(
  userId: string,
  target: SavedTarget
): Promise<{ success: boolean; error?: string }> {
  const accessToken = await getValidAccessToken();

  if (!accessToken) {
    return { success: false, error: 'Not authenticated' };
  }

  const { error } = await apiPost<DbSavedProperty[]>(
    'saved_properties',
    { user_id: userId, [savedTargetColumn(target.kind)]: target.id },
    accessToken
  );

  if (error) {
    // Check for duplicate (already saved)
    if (error.code === '23505') {
      return { success: true }; // Already saved, treat as success
    }
    console.error('Failed to save property:', error);
    return { success: false, error: error.message };
  }

  return { success: true };
}

/**
 * Unsave one target.
 *
 * Filtered on the column matching the KIND, so unsaving an indexed entry can
 * never delete a marketplace save that happens to share its uuid.
 */
export async function unsaveProperty(
  userId: string,
  target: SavedTarget
): Promise<{ success: boolean; error?: string }> {
  const accessToken = await getValidAccessToken();

  if (!accessToken) {
    return { success: false, error: 'Not authenticated' };
  }

  const { error } = await apiDelete(
    'saved_properties',
    `${eq('user_id', userId)}&${eq(savedTargetColumn(target.kind), target.id)}`,
    accessToken
  );

  if (error) {
    console.error('Failed to unsave property:', error);
    return { success: false, error: error.message };
  }

  return { success: true };
}

/**
 * Check if one target is saved.
 */
export async function isPropertySavedInDb(
  userId: string,
  target: SavedTarget
): Promise<boolean> {
  const accessToken = await getValidAccessToken();

  if (!accessToken) {
    return false;
  }

  const { data, error } = await apiGet<DbSavedProperty[]>(
    'saved_properties',
    `${eq('user_id', userId)}&${eq(savedTargetColumn(target.kind), target.id)}&select=id`,
    accessToken
  );

  if (error || !data) {
    return false;
  }

  return data.length > 0;
}
